import { useState, useMemo, useRef, useEffect, useCallback, memo } from 'react';
import {
  Truck, Clock, Download, Search, Navigation,
  Activity, Zap, MapPin, ShieldAlert,
  ArrowRight, ChevronDown, AlertTriangle,
  CalendarClock, Flag, ArrowUp, ArrowDown
} from 'lucide-react';
import { useUIStore } from '@/stores/uiStore';
import { useMapStore } from '@/stores/mapStore';
import { useChatStore } from '@/stores/chatStore';
import { triggerExcelDownload } from '@/api/ares';
import { formatAresDateTime, getAresWallClockMs } from '@/utils/aresDateTime';
import { getItem as getStoredItem, setItem as setStoredItem } from '@/utils/localStorage';
import { applyActiveRoutePatch } from '@/utils/fleetTripSync';
import {
  getVehicleBrandModel,
  getVehicleColorMeta,
  getVehicleTypeLabel,
} from '@/utils/vehicleIdentity';

const BASE_ROW_HEIGHT = 72;
const EXPANDED_ROW_HEIGHT = 168;
const OVERSCAN = 10;
const FLEET_GRID_COL_STORAGE_KEY = 'ares_fleet_grid_column_widths_v5';
const GRID_GAP_PX = 8;
const GRID_SIDE_PADDING_PX = 32;

function fmtDateTime(raw: string | null | undefined): { date: string; time: string } {
  const formatted = formatAresDateTime(raw);
  return {
    date: formatted.date === '-' ? '—' : formatted.date,
    time: formatted.time === '-' ? '—' : formatted.time,
  };
}

function getEffectiveEta(trip: any): string | null | undefined {
  return trip.eta_arrival_local || trip.estimated_arrival || trip.dashboard_eta_local;
}

function getEtaStatus(trip: any) {
  const etaMs = getAresWallClockMs(getEffectiveEta(trip));
  const citaMs = getAresWallClockMs(trip.destination_meeting_date);
  if (etaMs == null || citaMs == null) {
    return { label: 'Sin datos', dot: 'bg-text-muted' };
  }
  const diffMin = (citaMs - etaMs) / 60000;
  if (diffMin >= 30) return { label: 'A tiempo', dot: 'bg-accent-green' };
  if (diffMin >= 0) return { label: 'Ajustado', dot: 'bg-accent-orange' };
  return { label: 'Con retraso', dot: 'bg-accent-red' };
}

type GridColumnKey =
  | 'alias'
  | 'identifiers'
  | 'transport_line'
  | 'customer'
  | 'origin'
  | 'status'
  | 'theft_percent'
  | 'in_reaction'
  | 'estimated_arrival'
  | 'destination_meeting_date'
  | 'map';

type GridColumn = {
  key: GridColumnKey;
  label: string;
  defaultFlex?: number;
  minFlex?: number;
  fixedWidth?: number;
  sortKey?: string;
  align?: 'left' | 'center' | 'right';
  resizable?: boolean;
};

type GridColumnFlexState = Record<GridColumnKey, number>;

type RowMetric = {
  item: any;
  index: number;
  top: number;
  height: number;
};

const GRID_COLUMNS: GridColumn[] = [
  { key: 'alias', label: 'Unidad / Estado', defaultFlex: 2.25, minFlex: 1.45, sortKey: 'alias' },
  { key: 'identifiers', label: 'Velocidad', defaultFlex: 1.0, minFlex: 0.82, sortKey: 'current_speed' },
  { key: 'transport_line', label: 'Transportista', defaultFlex: 1.3, minFlex: 1.0, sortKey: 'transport_line' },
  { key: 'customer', label: 'Cliente / Destino', defaultFlex: 2.25, minFlex: 1.45, sortKey: 'customer' },
  { key: 'origin', label: 'Origen', defaultFlex: 1.2, minFlex: 0.95, sortKey: 'origin' },
  { key: 'status', label: 'Detalle', defaultFlex: 0.9, minFlex: 0.78, sortKey: 'estimated_arrival' },
  { key: 'theft_percent', label: 'Riesgo', defaultFlex: 1.02, minFlex: 0.86, sortKey: 'theft_percent', align: 'center' },
  { key: 'in_reaction', label: 'Reaccion', defaultFlex: 0.72, minFlex: 0.6, sortKey: 'in_reaction', align: 'center' },
  { key: 'estimated_arrival', label: 'ETA Llegada', defaultFlex: 1.28, minFlex: 1.05, sortKey: 'estimated_arrival' },
  { key: 'destination_meeting_date', label: 'Cita', defaultFlex: 1.22, minFlex: 1.0, sortKey: 'destination_meeting_date' },
  { key: 'map', label: 'Mapa', fixedWidth: 80, align: 'right', resizable: false },
];

function buildDefaultColumnFlexes(): GridColumnFlexState {
  return GRID_COLUMNS.reduce((acc, column) => {
    acc[column.key] = column.defaultFlex || 0;
    return acc;
  }, {} as GridColumnFlexState);
}

function sanitizeColumnFlexes(raw: Partial<GridColumnFlexState> | null | undefined): GridColumnFlexState {
  const defaults = buildDefaultColumnFlexes();
  const next = { ...defaults };

  GRID_COLUMNS.forEach((column) => {
    if (column.resizable === false) return;
    const candidate = raw?.[column.key];
    if (typeof candidate === 'number' && Number.isFinite(candidate)) {
      next[column.key] = Math.max(column.minFlex || 0.6, candidate);
    }
  });

  return next;
}

function getNextResizableColumnKey(key: GridColumnKey): GridColumnKey | null {
  const index = GRID_COLUMNS.findIndex((column) => column.key === key);
  if (index === -1) return null;

  for (let i = index + 1; i < GRID_COLUMNS.length; i += 1) {
    if (GRID_COLUMNS[i].resizable !== false) {
      return GRID_COLUMNS[i].key;
    }
  }

  return null;
}

const getRiskMeta = (risk: number) => {
  if (risk >= 75) return { cls: 'text-accent-red border-accent-red/40 bg-accent-red/5' };
  if (risk >= 50) return { cls: 'text-accent-orange border-accent-orange/40 bg-accent-orange/5' };
  return { cls: 'text-accent-green border-accent-green/40 bg-accent-green/5' };
};

const formatRiskPercent = (value: unknown) => {
  const numeric = Number(value ?? 0);
  if (!Number.isFinite(numeric)) return '0%';

  const truncated = Math.trunc(numeric * 100) / 100;
  const hasDecimals = !Number.isInteger(truncated);
  return `${truncated.toLocaleString('es-MX', {
    minimumFractionDigits: hasDecimals ? 2 : 0,
    maximumFractionDigits: hasDecimals ? 2 : 0,
  })}%`;
};

const DetailCard = ({ label, value, swatch }: { label: string; value: string; swatch?: string | null }) => (
  <div className="rounded-lg border border-border-subtle bg-bg-primary/60 px-3 py-2">
    <p className="text-[9px] font-black uppercase tracking-widest text-text-muted">{label}</p>
    <div className="mt-1 flex items-center gap-2 min-w-0">
      {swatch && (
        <span
          className="h-2.5 w-2.5 rounded-full border border-white/20 flex-shrink-0"
          style={{ backgroundColor: swatch }}
        />
      )}
      <p className="text-[11px] font-semibold text-text-primary truncate">{value}</p>
    </div>
  </div>
);

const TripRow = memo(({
  trip,
  isActive,
  isExpanded,
  onLocate,
  onToggleExpand,
  style,
  gridTemplateColumns,
  rowHeight,
}: any) => {
  const riskValue = Number(trip.theft_percent ?? 0);
  const risk = getRiskMeta(Number.isFinite(riskValue) ? riskValue : 0);
  const riskLabel = formatRiskPercent(trip.theft_percent);
  const eta = fmtDateTime(getEffectiveEta(trip));
  const cita = fmtDateTime(trip.destination_meeting_date);
  const etaSt = getEtaStatus(trip);
  const vehicleType = getVehicleTypeLabel(trip) || 'Sin dato';
  const vehicleBrand = trip.brand || 'Sin dato';
  const vehicleModel = trip.model || 'Sin dato';
  const vehicleColor = getVehicleColorMeta(trip);

  return (
    <div className="absolute left-0 right-0 px-1" style={{ ...style, height: `${rowHeight - 4}px` }}>
      <div
        className={`overflow-hidden rounded-xl border transition-colors duration-200 ${
          isActive || isExpanded
            ? 'bg-bg-secondary/80 border-border-strong shadow-sm'
            : 'bg-transparent border-transparent hover:bg-bg-secondary/40'
        }`}
      >
        <div
          role="button"
          tabIndex={0}
          onClick={() => onToggleExpand(trip.alias)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onToggleExpand(trip.alias);
            }
          }}
          className="w-full text-left cursor-pointer"
        >
          <div
            className="grid items-center px-3 py-3 gap-x-2"
            style={{ gridTemplateColumns, minHeight: `${BASE_ROW_HEIGHT - 4}px` }}
          >
            <div className="flex items-center gap-2 min-w-0">
              <div className={`w-8 h-8 rounded-lg border flex items-center justify-center flex-shrink-0 ${
                isActive || isExpanded
                  ? 'bg-bg-primary border-text-muted text-text-primary'
                  : 'bg-bg-secondary border-border-subtle text-text-muted'
              }`}>
                <Truck size={15} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5 min-w-0">
                  <ChevronDown
                    size={13}
                    className={`text-text-muted transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                  />
                  <span className="block text-xs font-black text-text-primary tracking-tight truncate">{trip.alias}</span>
                </div>
                <div className="flex items-center gap-1 mt-0.5 min-w-0">
                  <MapPin size={9} className="text-text-subtle flex-shrink-0" />
                  <span className="text-[10px] text-text-muted truncate font-medium">{trip.state || '—'}</span>
                </div>
              </div>
            </div>

            <div className="min-w-0">
              <div className="inline-flex items-center gap-1 rounded-full border border-border-subtle bg-bg-primary/60 px-2.5 py-1 text-[10px] font-semibold text-text-secondary">
                <Navigation size={10} className="text-text-subtle" />
                <span className="font-mono text-text-primary">{(trip.current_speed || 0).toFixed(0)} km/h</span>
              </div>
            </div>

            <div className="min-w-0">
              <span className="block text-xs font-semibold text-text-primary truncate">{trip.transport_line || '—'}</span>
            </div>

            <div className="min-w-0">
              <span className="block text-xs font-black text-text-primary truncate uppercase tracking-tight">{trip.customer || '—'}</span>
              <div className="flex items-center gap-1 mt-0.5">
                <ArrowRight size={9} className="text-text-subtle flex-shrink-0" />
                <span className="text-[10px] text-text-muted truncate">{trip.destination_name || 'Sin destino'}</span>
              </div>
            </div>

            <div className="min-w-0">
              <div className="flex items-center gap-1">
                <Flag size={9} className="text-text-subtle flex-shrink-0" />
                <span className="text-xs font-semibold text-text-secondary truncate">{trip.origin || '—'}</span>
              </div>
            </div>

            <div className="min-w-0">
              <span className="inline-flex items-center gap-1 rounded-full border border-border-subtle bg-bg-primary/60 px-2 py-1 text-[9px] font-semibold uppercase tracking-wide text-text-secondary">
                <ChevronDown size={10} className={`transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                Ver detalle
              </span>
            </div>

            <div className="flex justify-center min-w-0">
              <div className={`inline-flex items-center gap-1 rounded-lg border px-2.5 py-1.5 ${risk.cls}`}>
                <ShieldAlert size={10} />
                <span className="text-xs font-black leading-none tabular-nums">{riskLabel}</span>
              </div>
            </div>

            <div className="flex justify-center min-w-0">
              {trip.in_reaction ? (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md border border-accent-orange/50 text-accent-orange text-[10px] font-bold uppercase">
                  <AlertTriangle size={9} />
                  Sí
                </span>
              ) : (
                <span className="text-[10px] font-medium text-text-subtle uppercase">No</span>
              )}
            </div>

            <div className="min-w-0">
              <div className="flex items-center gap-1.5">
                <CalendarClock size={10} className="text-text-subtle flex-shrink-0" />
                <span className="text-xs font-black text-text-primary font-mono">{eta.time}</span>
                <span className="text-[10px] text-text-muted">{eta.date}</span>
              </div>
              <div className="flex items-center gap-1.5 mt-0.5">
                <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${etaSt.dot}`} />
                <span className="text-[10px] font-medium uppercase truncate text-text-secondary">{etaSt.label}</span>
              </div>
            </div>

            <div className="min-w-0">
              <div className="flex items-center gap-1.5">
                <Clock size={10} className="text-text-subtle flex-shrink-0" />
                <span className="text-xs font-black text-text-primary font-mono">{cita.time}</span>
                <span className="text-[10px] text-text-muted">{cita.date}</span>
              </div>
            </div>

            <div className="flex justify-end">
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onLocate(trip);
                }}
                title="Ver en mapa"
                className={`flex items-center gap-1.5 h-7 px-3 rounded-lg border transition-colors duration-200 ${
                  isActive
                    ? 'bg-text-primary text-bg-primary border-text-primary'
                    : 'bg-transparent border-border-subtle text-text-muted hover:border-text-secondary hover:text-text-primary'
                }`}
              >
                <Navigation size={13} />
                <span className="text-[10px] font-bold">{isActive ? '✓' : 'Ver'}</span>
              </button>
            </div>
          </div>
        </div>

        {isExpanded && (
          <div className="border-t border-border-subtle/50 bg-bg-secondary/70 px-4 py-3">
            <div className="grid grid-cols-2 lg:grid-cols-6 gap-2">
              <DetailCard label="Tipo" value={vehicleType} />
              <DetailCard label="Marca" value={vehicleBrand} />
              <DetailCard label="Modelo" value={vehicleModel} />
              <DetailCard label="Color" value={vehicleColor?.label || trip.color || 'Sin dato'} swatch={vehicleColor?.swatch} />
              <DetailCard label="Eco" value={trip.economic || 'Sin dato'} />
              <DetailCard label="Placa" value={trip.plate || 'Sin dato'} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
});

TripRow.displayName = 'TripRow';

export default function FleetGridPanel() {
  const { fleetData, setFleetPanelOpen, currentExportId } = useUIStore();
  const setHighlightPosition = useMapStore((s) => s.setHighlightPosition);
  const routeData = useMapStore((s) => s.routeData);
  const isLoading = useChatStore((s) => s.isLoading);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeAlias, setActiveAlias] = useState<string | null>(null);
  const [expandedAlias, setExpandedAlias] = useState<string | null>(null);
  const gridViewportRef = useRef<HTMLDivElement>(null);
  const gridViewportWidthRef = useRef(0);
  const storedColumnFlexesRef = useRef<Partial<GridColumnFlexState> | null>(
    getStoredItem<Partial<GridColumnFlexState> | null>(FLEET_GRID_COL_STORAGE_KEY, null)
  );
  const [columnFlexes, setColumnFlexes] = useState<GridColumnFlexState>(() =>
    sanitizeColumnFlexes(storedColumnFlexesRef.current)
  );
  const activeResizeRef = useRef<{
    key: GridColumnKey;
    nextKey: GridColumnKey;
    startX: number;
    startCurrentFlex: number;
    startNextFlex: number;
    totalFlex: number;
  } | null>(null);
  const hasManualResizeRef = useRef(Boolean(storedColumnFlexesRef.current));

  useEffect(() => {
    if (isLoading) {
      setSearchTerm('');
    }
  }, [isLoading]);

  const scrollRef = useRef<HTMLDivElement>(null);
  const [scrollTop, setScrollTop] = useState(0);
  const [containerHeight, setContainerHeight] = useState(600);
  const [gridViewportWidth, setGridViewportWidth] = useState(0);

  const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' | null }>({
    key: 'alias',
    direction: 'asc'
  });

  const gridTemplateColumns = useMemo(
    () =>
      GRID_COLUMNS.map((column) =>
        column.resizable === false
          ? `${column.fixedWidth || 80}px`
          : `minmax(0, ${columnFlexes[column.key]}fr)`
      ).join(' '),
    [columnFlexes]
  );

  const totalResizableFlex = useMemo(
    () =>
      GRID_COLUMNS
        .filter((column) => column.resizable !== false)
        .reduce((sum, column) => sum + columnFlexes[column.key], 0),
    [columnFlexes]
  );

  const onScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    setScrollTop(e.currentTarget.scrollTop);
  }, []);

  const startColumnResize = useCallback((key: GridColumnKey, e: React.MouseEvent<HTMLDivElement>) => {
    const nextKey = getNextResizableColumnKey(key);
    if (!nextKey) return;

    e.preventDefault();
    e.stopPropagation();
    activeResizeRef.current = {
      key,
      nextKey,
      startX: e.clientX,
      startCurrentFlex: columnFlexes[key],
      startNextFlex: columnFlexes[nextKey],
      totalFlex: totalResizableFlex,
    };
    hasManualResizeRef.current = true;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, [columnFlexes, totalResizableFlex]);

  const resetGridLayout = useCallback(() => {
    hasManualResizeRef.current = false;
    setColumnFlexes(buildDefaultColumnFlexes());
  }, []);

  useEffect(() => {
    if (scrollRef.current) {
      setContainerHeight(scrollRef.current.clientHeight);

      const resizeObserver = new ResizeObserver((entries) => {
        for (const entry of entries) {
          setContainerHeight(entry.contentRect.height);
        }
      });
      resizeObserver.observe(scrollRef.current);
      return () => resizeObserver.disconnect();
    }
  }, []);

  useEffect(() => {
    if (!gridViewportRef.current) return;

    const updateWidth = () => {
      if (!gridViewportRef.current) return;
      const nextWidth = Math.max(0, Math.floor(gridViewportRef.current.clientWidth - 16));
      gridViewportWidthRef.current = nextWidth;
      setGridViewportWidth(nextWidth);
    };

    updateWidth();

    const resizeObserver = new ResizeObserver(() => updateWidth());
    resizeObserver.observe(gridViewportRef.current);
    window.addEventListener('resize', updateWidth);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', updateWidth);
    };
  }, []);

  useEffect(() => {
    if (!hasManualResizeRef.current) {
      setColumnFlexes(buildDefaultColumnFlexes());
    }
  }, [gridViewportWidth]);

  useEffect(() => {
    setStoredItem(FLEET_GRID_COL_STORAGE_KEY, columnFlexes);
  }, [columnFlexes]);

  useEffect(() => {
    const onMouseMove = (e: MouseEvent) => {
      const activeResize = activeResizeRef.current;
      if (!activeResize) return;

      const currentColumn = GRID_COLUMNS.find((item) => item.key === activeResize.key);
      const nextColumn = GRID_COLUMNS.find((item) => item.key === activeResize.nextKey);
      const currentViewportWidth = gridViewportWidthRef.current;
      if (!currentColumn || !nextColumn || currentViewportWidth <= 0) return;

      const fixedWidth = GRID_COLUMNS
        .filter((column) => column.resizable === false)
        .reduce((sum, column) => sum + (column.fixedWidth || 0), 0);
      const gapWidth = (GRID_COLUMNS.length - 1) * GRID_GAP_PX;
      const usableWidth = Math.max(currentViewportWidth - fixedWidth - gapWidth - GRID_SIDE_PADDING_PX, 1);
      const deltaFlex = ((e.clientX - activeResize.startX) / usableWidth) * activeResize.totalFlex;

      const minCurrent = currentColumn.minFlex || 0.6;
      const minNext = nextColumn.minFlex || 0.6;
      const combinedFlex = activeResize.startCurrentFlex + activeResize.startNextFlex;

      let nextCurrentFlex = activeResize.startCurrentFlex + deltaFlex;
      nextCurrentFlex = Math.max(minCurrent, Math.min(combinedFlex - minNext, nextCurrentFlex));
      const nextNextFlex = combinedFlex - nextCurrentFlex;

      setColumnFlexes((prev) => {
        if (
          Math.abs(prev[activeResize.key] - nextCurrentFlex) < 0.001 &&
          Math.abs(prev[activeResize.nextKey] - nextNextFlex) < 0.001
        ) {
          return prev;
        }

        return {
          ...prev,
          [activeResize.key]: nextCurrentFlex,
          [activeResize.nextKey]: nextNextFlex,
        };
      });
    };

    const onMouseUp = () => {
      if (!activeResizeRef.current) return;
      activeResizeRef.current = null;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      window.dispatchEvent(new Event('resize'));
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);

    return () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [columnFlexes]);

  const sortedData = useMemo(() => {
    let data = [...applyActiveRoutePatch(fleetData, routeData)];

    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      data = data.filter((t: any) =>
        t.alias?.toLowerCase().includes(q) ||
        t.plate?.toLowerCase().includes(q) ||
        t.economic?.toLowerCase().includes(q) ||
        t.type?.toLowerCase().includes(q) ||
        t.brand?.toLowerCase().includes(q) ||
        t.model?.toLowerCase().includes(q) ||
        t.color?.toLowerCase().includes(q) ||
        t.customer?.toLowerCase().includes(q) ||
        t.transport_line?.toLowerCase().includes(q) ||
        t.destination_name?.toLowerCase().includes(q) ||
        t.origin?.toLowerCase().includes(q) ||
        t.state?.toLowerCase().includes(q)
      );
    }

    if (sortConfig.key && sortConfig.direction) {
      data.sort((a: any, b: any) => {
        let aVal = a[sortConfig.key];
        let bVal = b[sortConfig.key];

        if (sortConfig.key === 'eta_status') {
          aVal = getEtaStatus(a).label;
          bVal = getEtaStatus(b).label;
        } else if (sortConfig.key === 'estimated_arrival') {
          aVal = getEffectiveEta(a);
          bVal = getEffectiveEta(b);
        }

        if (aVal === bVal) return 0;
        if (aVal === null || aVal === undefined) return 1;
        if (bVal === null || bVal === undefined) return -1;

        const comparison = aVal < bVal ? -1 : 1;
        return sortConfig.direction === 'asc' ? comparison : -comparison;
      });
    }

    return data;
  }, [fleetData, routeData, searchTerm, sortConfig]);

  useEffect(() => {
    if (expandedAlias && !sortedData.some((item: any) => item.alias === expandedAlias)) {
      setExpandedAlias(null);
    }
  }, [expandedAlias, sortedData]);

  const rowMetrics = useMemo(() => {
    let top = 0;
    const metrics: RowMetric[] = sortedData.map((item: any, index: number) => {
      const height = item.alias === expandedAlias ? EXPANDED_ROW_HEIGHT : BASE_ROW_HEIGHT;
      const metric = { item, index, top, height };
      top += height;
      return metric;
    });
    return { metrics, totalHeight: top };
  }, [sortedData, expandedAlias]);

  const { visibleRows, totalHeight } = useMemo(() => {
    const overscanPx = OVERSCAN * BASE_ROW_HEIGHT;
    const viewportTop = Math.max(0, scrollTop - overscanPx);
    const viewportBottom = scrollTop + containerHeight + overscanPx;

    return {
      visibleRows: rowMetrics.metrics.filter((metric) => (
        metric.top + metric.height >= viewportTop && metric.top <= viewportBottom
      )),
      totalHeight: rowMetrics.totalHeight,
    };
  }, [rowMetrics, scrollTop, containerHeight]);

  const requestSort = (key: string) => {
    let direction: 'asc' | 'desc' = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortIcon = (key: string) => {
    if (sortConfig.key !== key) return <div className="w-3" />;
    return sortConfig.direction === 'asc'
      ? <ArrowUp size={10} className="ml-1" />
      : <ArrowDown size={10} className="ml-1" />;
  };

  const renderHeaderCell = (column: GridColumn) => {
    const isSortable = Boolean(column.sortKey);
    const canResize = column.resizable !== false && Boolean(getNextResizableColumnKey(column.key));
    const alignmentClass =
      column.align === 'center'
        ? 'justify-center text-center'
        : column.align === 'right'
          ? 'justify-end text-right pr-1'
          : '';

    return (
      <div
        key={column.key}
        className={`relative min-w-0 flex items-center pr-2 ${alignmentClass} ${
          isSortable ? 'cursor-pointer hover:text-accent-purple transition-colors' : ''
        }`}
        onClick={isSortable ? () => requestSort(column.sortKey!) : undefined}
      >
        <span className="truncate">{column.label}</span>
        {column.sortKey ? getSortIcon(column.sortKey) : null}
        {canResize && (
          <div
            role="separator"
            aria-orientation="vertical"
            title="Arrastra para ajustar ancho. Doble click para resetear el grid."
            className="absolute -right-2 top-0 flex h-full w-4 cursor-col-resize items-center justify-center"
            onMouseDown={(e) => startColumnResize(column.key, e)}
            onDoubleClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              resetGridLayout();
            }}
          >
            <div className="h-5/6 w-px rounded-full bg-border-subtle transition-colors hover:bg-accent-purple" />
          </div>
        )}
      </div>
    );
  };

  const handleLocateOnMap = useCallback((trip: any) => {
    const lat = trip.latitude || trip.lat || trip.current_lat;
    const lon = trip.longitude || trip.lon || trip.current_lon;
    if (lat && lon) {
      setHighlightPosition([lat, lon]);
      window.dispatchEvent(new CustomEvent('center-map', { detail: { lat, lon, zoom: 14 } }));
      setActiveAlias(trip.alias);
      setTimeout(() => setHighlightPosition(null), 4000);
    }
  }, [setHighlightPosition]);

  const handleToggleExpand = useCallback((alias: string) => {
    setExpandedAlias((current) => current === alias ? null : alias);
  }, []);

  const handleDownloadReport = () => {
    if (currentExportId) {
      triggerExcelDownload(currentExportId);
    }
  };

  const criticalCount = sortedData.filter((t: any) => (t.theft_percent || 0) > 75).length;
  const inReactionCount = sortedData.filter((t: any) => t.in_reaction).length;
  const avgSpeed = sortedData.length
    ? (sortedData.reduce((s: number, t: any) => s + (t.current_speed || 0), 0) / sortedData.length).toFixed(0)
    : '0';

  return (
    <div className="h-full flex flex-col bg-bg-primary overflow-hidden">
      <div className="flex items-center justify-between px-5 pt-3 pb-2.5 border-b border-border-subtle flex-shrink-0 bg-bg-secondary/60">
        <div className="flex items-center gap-3">
          <div className="relative w-8 h-8 rounded-xl bg-bg-secondary border border-border-subtle flex items-center justify-center text-accent-purple flex-shrink-0">
            <Activity size={15} className="animate-pulse" />
            <div className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-accent-green border-2 border-bg-primary" />
          </div>
          <div>
            <h2 className="text-sm font-black text-text-primary tracking-tight uppercase leading-none">
              Centro de Comando de Flota
            </h2>
            <div className="flex items-center gap-2 mt-0.5 flex-wrap">
              <span className="text-[9px] font-bold text-accent-green uppercase tracking-widest">{fleetData.length} activos</span>
              <span className="w-1 h-1 rounded-full bg-border-subtle" />
              <span className="text-[9px] text-text-muted uppercase tracking-widest">Vel. media: {avgSpeed} km/h</span>
              {criticalCount > 0 && (
                <>
                  <span className="w-1 h-1 rounded-full bg-border-subtle" />
                  <span className="text-[9px] font-bold text-accent-red uppercase tracking-widest animate-pulse">{criticalCount} críticos</span>
                </>
              )}
              {inReactionCount > 0 && (
                <>
                  <span className="w-1 h-1 rounded-full bg-border-subtle" />
                  <span className="text-[9px] font-bold text-accent-orange uppercase tracking-widest">{inReactionCount} en reacción</span>
                </>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <div className="relative group hidden md:flex items-center">
            <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-text-subtle group-focus-within:text-accent-purple transition-colors pointer-events-none" />
            <input
              type="text"
              placeholder="Buscar alias, placa, eco, marca..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="h-7 bg-bg-secondary border border-border-subtle rounded-xl pl-8 pr-3 text-[11px] text-text-primary w-56
                         placeholder:text-text-muted focus:outline-none focus:border-accent-purple/50 focus:ring-2 focus:ring-accent-purple/10 transition-all"
            />
          </div>
          <button
            disabled={!currentExportId}
            onClick={handleDownloadReport}
            className={`h-7 flex items-center gap-1.5 px-3 rounded-xl transition-all text-[10px] font-bold ${
              currentExportId
                ? 'bg-accent-blue/10 border border-accent-blue/30 text-accent-blue hover:bg-accent-blue hover:text-white'
                : 'bg-bg-secondary border border-border-subtle text-text-muted cursor-not-allowed opacity-50'
            }`}
          >
            <Download size={12} />
            <span>Reporte</span>
          </button>
          <button
            onClick={() => setFleetPanelOpen(false)}
            className="w-7 h-7 rounded-xl border border-border-subtle text-text-muted hover:text-accent-red hover:bg-accent-red/10 hover:border-accent-red/30 transition-all flex items-center justify-center group"
          >
            <ChevronDown size={15} className="group-hover:scale-110 transition-transform" />
          </button>
        </div>
      </div>

      <div ref={gridViewportRef} className="flex-1 min-h-0 overflow-hidden">
        <div className="flex w-full h-full flex-col">
          <div
            className="grid items-center px-4 py-2 text-[9px] font-black uppercase tracking-[0.14em] text-text-subtle border-b border-border-subtle/40 flex-shrink-0 bg-bg-primary/50 gap-x-2"
            style={{ gridTemplateColumns }}
          >
            {GRID_COLUMNS.map(renderHeaderCell)}
          </div>

          <div
            ref={scrollRef}
            onScroll={onScroll}
            className="flex-1 overflow-y-auto custom-scrollbar px-2 py-1.5 min-h-0"
          >
            {sortedData.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-24 text-text-muted">
                <Search size={22} className="opacity-30 mb-2" />
                <p className="text-xs font-semibold">Sin resultados para "{searchTerm}"</p>
              </div>
            ) : (
              <div className="relative w-full" style={{ height: `${totalHeight}px` }}>
                {visibleRows.map((row) => (
                  <TripRow
                    key={row.item.alias || row.index}
                    trip={row.item}
                    isActive={activeAlias === row.item.alias}
                    isExpanded={expandedAlias === row.item.alias}
                    onLocate={handleLocateOnMap}
                    onToggleExpand={handleToggleExpand}
                    gridTemplateColumns={gridTemplateColumns}
                    rowHeight={row.height}
                    style={{ top: `${row.top}px` }}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between px-5 py-2 border-t border-border-subtle/50 bg-bg-secondary/40 flex-shrink-0">
        <div className="flex items-center gap-6">
          <div>
            <p className="text-[8px] font-black uppercase tracking-widest text-text-muted">Registros</p>
            <p className="text-lg font-black text-text-primary leading-none">{sortedData.length}</p>
          </div>
          <div>
            <p className="text-[8px] font-black uppercase tracking-widest text-accent-red/60">Alertas críticas</p>
            <p className="text-lg font-black text-accent-red leading-none">{criticalCount}</p>
          </div>
          {inReactionCount > 0 && (
            <div>
              <p className="text-[8px] font-black uppercase tracking-widest text-accent-orange/60">En reacción</p>
              <p className="text-lg font-black text-accent-orange leading-none">{inReactionCount}</p>
            </div>
          )}
          <div>
            <p className="text-[8px] font-black uppercase tracking-widest text-text-muted">Vel. media</p>
            <p className="text-lg font-black text-text-primary leading-none">{avgSpeed} <span className="text-xs font-bold text-text-muted">km/h</span></p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Zap size={10} className="text-accent-purple animate-pulse" />
          <span className="text-[8px] font-mono text-text-subtle italic uppercase tracking-widest hidden md:block">
            Ares Fleet v7.5 · Motor Multi-Tema
          </span>
        </div>
      </div>
    </div>
  );
}
